import React, { useEffect } from "react";
import "./ProductsDrawer.less";
import { DrawerProps } from "antd/lib/drawer";
import { FormComponentProps } from "@ant-design/compatible/lib/form";
import { Form } from "@ant-design/compatible";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import Input from "antd/lib/input";
import TextArea from "antd/lib/input/TextArea";
import Select from "antd/lib/select";
import Skeleton from "antd/lib/skeleton";
import {
	ProductPricingPlan,
	ProductBillingType,
	productBillingTypeOptions
} from "@/Contracts/Products";
import { getCurrencySettings } from "@/Utils/Currency";
import {
	handleMarkupMargin,
	handleMarkupChange,
	handleMarginChange
} from "../../Utils/PricingCalculator";
import ZDrawer from "@molecules/ZDrawer";
import ZButton from "@atoms/ZButton";
import ZInput from "@molecules/ZInput";
import ZInputNumber from "@atoms/ZInputNumber";
import ZTypography from "@atoms/ZTypography";
export interface Props
	extends DrawerProps,
		FormComponentProps<ProductPricingPlan> {
	readonly isLoading: boolean;
	readonly currentActiveProductPricingPlan: ProductPricingPlan | null;
	readonly onSubmit: (productPricingPlan: ProductPricingPlan) => void;
	readonly isReadonly: boolean;
}

const { ZText } = ZTypography;

export const EditProductPricingPlanDrawerForm: React.FC<Props> = props => {
	const populateMarkupMargin = (sellPrice: number) => {
		const values = getFieldValues();
		if (!values) return;
		const response = handleMarkupMargin(
			values.cost_price,
			sellPrice,
			values.quantity,
			values.should_add_setup_price_to_margin,
			values.setup_price
		);
		return response;
	};

	const onClose = (
		$event: React.MouseEvent<any> | React.KeyboardEvent<HTMLDivElement>
	) => {
		if (props.onClose) {
			props.onClose($event as any);
		}
	};

	const getFieldValues = () => {
		return props.form.getFieldsValue([
			"cost_price",
			"sell_price",
			"should_add_setup_price_to_margin",
			"setup_price",
			"margin",
			"markup",
			"vendor_name",
			"vendor_sku_no"
		]);
	};

	const onCostPriceChange = (newCostPrice?: number | string | null): void => {
		if (newCostPrice === undefined || newCostPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				Number(newCostPrice),
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.selected_setup_price
			)
		);
	};

	const onMarkupChange = (newMarkup?: number | string | null): void => {
		if (
			newMarkup === undefined ||
			props.form.getFieldValue("cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupChange(values, Number(newMarkup))
		);
	};

	const onMarginChange = (newMargin?: number | string | null): void => {
		if (
			newMargin === undefined ||
			newMargin === null ||
			props.form.getFieldValue("cost_price") === 0
		) {
			return;
		}
		const values = getFieldValues();
		if (
			Number(newMargin) >= 100 &&
			Number(getFieldValues().cost_price) !== 0
		) {
			return;
		}
		props.form.setFieldsValue(
			handleMarginChange(values, Number(newMargin))
		);
	};

	const onSellPriceChange = (newSellPrice?: number | string | null): void => {
		if (newSellPrice === undefined || newSellPrice === null) {
			return;
		}
		const values = getFieldValues();
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				Number(newSellPrice),
				values.quantity,
				values.should_add_setup_price_to_margin,
				values.setup_price
			)
		);
	};

	const onSetupPriceChange = (
		newSetupPrice?: number | string | null
	): void => {
		if (newSetupPrice === undefined || newSetupPrice === null) {
			return;
		}
		const values = getFieldValues();
		if (!values.should_add_setup_price_to_margin) {
			return;
		}
		props.form.setFieldsValue(
			handleMarkupMargin(
				values.cost_price,
				values.sell_price,
				values.quantity,
				values.should_add_setup_price_to_margin,
				Number(newSetupPrice)
			)
		);
	};

	const onSubmit = ($event: React.FormEvent<HTMLFormElement>) => {
		$event.preventDefault();
		props.form.validateFields(
			(errors: unknown, editProductPricingPlan: ProductPricingPlan) => {
				if (!errors) {
					editProductPricingPlan.id =
						props.currentActiveProductPricingPlan &&
						props.currentActiveProductPricingPlan.id &&
						props.currentActiveProductPricingPlan?.id?.length > 0
							? props.currentActiveProductPricingPlan.id
							: null;
					editProductPricingPlan.item_id =
						props.currentActiveProductPricingPlan &&
						props.currentActiveProductPricingPlan.item_id &&
						props.currentActiveProductPricingPlan.item_id.length > 0
							? props.currentActiveProductPricingPlan.item_id
							: null;
					editProductPricingPlan.session_id =
						props.currentActiveProductPricingPlan &&
						props.currentActiveProductPricingPlan.session_id &&
						props.currentActiveProductPricingPlan.session_id
							.length > 0
							? props.currentActiveProductPricingPlan.session_id
							: null;
					props.onSubmit(editProductPricingPlan);
				}
			}
		);
	};

	useEffect(() => {
		props.currentActiveProductPricingPlan?.sell_price &&
			populateMarkupMargin(
				props.currentActiveProductPricingPlan?.sell_price
			);
	}, [props.currentActiveProductPricingPlan]);

	const { isReadonly } = props;

	return (
		<ZDrawer
			visible={props.visible}
			closable
			onClose={onClose}
			destroyOnClose
			className="edit-pricing-plan-drawer zomentum-drawer"
			title={
				<Row>
					<Col span={24}>
						<div className="drawer-title-container">
							<ZText className="drawer-title">
								Edit Pricing Plan
							</ZText>
						</div>
					</Col>
				</Row>
			}
		>
			<Skeleton
				loading={!props.currentActiveProductPricingPlan}
				active
				paragraph={{ rows: 12 }}
			>
				<Form
					layout="horizontal"
					labelCol={{ span: 5 }}
					wrapperCol={{ span: 19 }}
					onSubmit={onSubmit}
					className="edit-pricing-plan-drawer-form pb-6"
				>
					<Row className="drawer-form-container">
						<Col span={24}>
							<Form.Item
								label="Name"
								labelAlign="left"
								hasFeedback={true}
							>
								<Input.Group>
									{props.form.getFieldDecorator("name", {
										rules: [
											{
												required: true,
												message:
													"Please enter pricing plan name",
												whitespace: true
											}
										],
										initialValue:
											props.currentActiveProductPricingPlan
												? props
														.currentActiveProductPricingPlan
														.name
												: ""
									})(
										<ZInput
											disabled={isReadonly}
											placeholder="Please enter pricing plan name"
										/>
									)}
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Description"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								{props.form.getFieldDecorator("description", {
									rules: [
										{
											required: false,
											message:
												"Please enter pricing plan description",
											whitespace: true
										}
									],
									initialValue:
										props.currentActiveProductPricingPlan
											? props
													.currentActiveProductPricingPlan
													.description
											: ""
								})(
									<TextArea
										disabled={isReadonly}
										placeholder="Please enter pricing plan description"
										autoSize={{ minRows: 3 }}
										allowClear={true}
										className="zomentum-sm-textarea"
									/>
								)}
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Billing Period"
								labelAlign="left"
								hasFeedback={true}
								wrapperCol={{ span: 12 }}
								colon={false}
							>
								{props.form.getFieldDecorator(
									"billing_period",
									{
										rules: [
											{
												required: true,
												message:
													"Please select billing period"
											}
										],
										initialValue:
											props.currentActiveProductPricingPlan
												? props
														.currentActiveProductPricingPlan
														.billing_period
												: ""
									}
								)(
									<Select<ProductBillingType>
										getPopupContainer={trigger =>
											trigger.parentNode
										}
										disabled={isReadonly}
										allowClear={true}
										className="w-5/6"
										placeholder="Please select billing period"
									>
										{(
											Object.keys(
												productBillingTypeOptions
											) as ProductBillingType[]
										).map((value, index) => (
											<Select.Option
												key={index}
												value={value}
											>
												{
													productBillingTypeOptions[
														value
													]
												}
											</Select.Option>
										))}
									</Select>
								)}
							</Form.Item>
						</Col>

						<Col span={24}>
							<Form.Item
								label="Cost Price"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator(
										"cost_price",
										{
											rules: [
												{
													required: true,
													validator: (_, value) => {
														if (Number(value) < 0) {
															return Promise.reject(
																new Error(
																	"Invalid Entry. Cost price cannot be less than 0"
																)
															);
														}
														if (
															!value &&
															value !== 0
														) {
															return Promise.reject(
																new Error(
																	"Cost price cannot be empty"
																)
															);
														}
														return Promise.resolve();
													}
												}
											],
											initialValue:
												props.currentActiveProductPricingPlan &&
												props
													.currentActiveProductPricingPlan
													.cost_price &&
												props
													.currentActiveProductPricingPlan
													.cost_price > 0
													? props
															.currentActiveProductPricingPlan
															.cost_price
													: 0
										}
									)(
										<ZInputNumber
											disabled={isReadonly}
											type="number"
											placeholder="Please enter cost price"
											min={0}
											precision={2}
											step={0.01}
											onChange={onCostPriceChange}
										/>
									)}
									<ZInput
										placeholder={
											getCurrencySettings().currency
										}
										disabled
										className="currency-addon"
									/>
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Markup"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator("markup", {
										initialValue:
											props.currentActiveProductPricingPlan &&
											props
												.currentActiveProductPricingPlan
												.sell_price &&
											props
												.currentActiveProductPricingPlan
												.sell_price > 0 &&
											populateMarkupMargin(
												props
													.currentActiveProductPricingPlan
													.sell_price
											)?.markup,
										rules: [
											{
												required: false,
												type: "number"
											}
										]
									})(
										<ZInputNumber
											disabled={isReadonly}
											placeholder="Please enter product Markup"
											precision={2}
											onChange={onMarkupChange}
										/>
									)}
									<ZInput
										placeholder={"%"}
										disabled
										className="currency-addon"
									/>
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Margin"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator("margin", {
										initialValue:
											props.currentActiveProductPricingPlan &&
											props
												.currentActiveProductPricingPlan
												.sell_price &&
											props
												.currentActiveProductPricingPlan
												.sell_price > 0 &&
											populateMarkupMargin(
												props
													.currentActiveProductPricingPlan
													.sell_price
											)?.margin,
										rules: [
											{
												required: false,
												validator: (_, value) => {
													if (
														Number(value) >= 100 &&
														props.form.getFieldValue(
															"cost_price"
														) > 0
													) {
														return Promise.reject(
															new Error(
																"Invalid Entry. Margin cannot be 100% unless cost price is 0"
															)
														);
													}
													return Promise.resolve();
												}
											}
										]
									})(
										<ZInputNumber
											disabled={isReadonly}
											placeholder="Please enter product Margin"
											max={100}
											precision={2}
											onChange={onMarginChange}
											step={0.1}
										/>
									)}
									<ZInput
										placeholder={"%"}
										disabled
										className="currency-addon"
									/>
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Selling Price"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator(
										"sell_price",
										{
											rules: [
												{
													required: true,
													message:
														"Please enter selling price"
												}
											],
											initialValue:
												props.currentActiveProductPricingPlan &&
												props
													.currentActiveProductPricingPlan
													.sell_price &&
												props
													.currentActiveProductPricingPlan
													.sell_price > 0
													? props
															.currentActiveProductPricingPlan
															.sell_price
													: 0
										}
									)(
										<ZInputNumber
											disabled={isReadonly}
											type="number"
											placeholder="Please enter selling price"
											min={0}
											precision={2}
											step={0.01}
											onChange={onSellPriceChange}
										/>
									)}
									<ZInput
										placeholder={
											getCurrencySettings().currency
										}
										disabled
										className="currency-addon"
									/>
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Setup Fee"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator(
										"setup_price",
										{
											rules: [
												{
													required: true,
													message:
														"Please enter one time setup fee"
												}
											],
											initialValue:
												props.currentActiveProductPricingPlan &&
												props
													.currentActiveProductPricingPlan
													.setup_price &&
												props
													.currentActiveProductPricingPlan
													.setup_price > 0
													? props
															.currentActiveProductPricingPlan
															.setup_price
													: 0
										}
									)(
										<ZInputNumber
											disabled={isReadonly}
											type="number"
											placeholder="Please enter one time setup fee"
											min={0}
											precision={2}
											step={0.01}
											onChange={onSetupPriceChange}
										/>
									)}
									<ZInput
										placeholder={
											getCurrencySettings().currency
										}
										disabled
										className="currency-addon"
									/>
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item
								label="Vendor Name"
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator(
										"vendor_name",
										{
											rules: [],
											initialValue:
												props
													.currentActiveProductPricingPlan
													?.vendor_name ?? ""
										}
									)(
										<ZInput placeholder="Please enter Vendor Name" />
									)}
								</Input.Group>
							</Form.Item>
						</Col>
						<Col span={24} className="mb-3">
							<Form.Item
								label="Vendor SKU NO."
								labelAlign="left"
								hasFeedback={true}
								colon={false}
							>
								<Input.Group>
									{props.form.getFieldDecorator(
										"vendor_sku_no",
										{
											rules: [
												{
													required: false,
													message:
														"Please enter Vendor SKU No",
													whitespace: true
												}
											],
											initialValue:
												props
													.currentActiveProductPricingPlan
													?.vendor_sku_no ?? ""
										}
									)(
										<ZInput placeholder="Please enter Vendor SKU No" />
									)}
								</Input.Group>
							</Form.Item>
						</Col>
					</Row>
					<div className="ant-drawer-footer">
						<ZButton
							type="default"
							htmlType="button"
							onClick={onClose}
							disabled={props.isLoading}
							className={isReadonly ? "" : "mr-4"}
						>
							{isReadonly ? "Close" : "Cancel"}
						</ZButton>
						{!isReadonly && (
							<ZButton
								type="primary"
								htmlType="submit"
								disabled={props.isLoading}
								loading={props.isLoading}
							>
								Save
							</ZButton>
						)}
					</div>
				</Form>
			</Skeleton>
		</ZDrawer>
	);
};

export default Form.create<Props>({ name: "edit_product_pricing_plan_form" })(
	EditProductPricingPlanDrawerForm
);
